import React, { useState } from 'react';
import { Calendar, MapPin, Clock, Users, Search, Filter, Bookmark, ExternalLink, Globe } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { mockEvents } from '../data/mock';
import { toast } from '../hooks/use-toast';

const Events = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [attendingEvents, setAttendingEvents] = useState(['1']);
  const [savedEvents, setSavedEvents] = useState([]);

  const allEvents = [
    ...mockEvents,
    {
      id: "3",
      title: "AI-Powered Learning Summit",
      date: "April 5, 2024",
      time: "10:00 AM - 6:00 PM",
      location: "San Francisco, CA",
      attendees: 750,
      image: "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=300&h=200&fit=crop",
      description: "Discover how AI is transforming the learning and development landscape",
      type: "Conference",
      price: "Free"
    },
    {
      id: "4", 
      title: "Remote Training Best Practices",
      date: "April 12, 2024",
      time: "1:00 PM - 3:00 PM",
      location: "Virtual Event",
      attendees: 320,
      image: "https://images.unsplash.com/photo-1588196749597-9ff075ee6b5b?w=300&h=200&fit=crop",
      description: "Learn effective strategies for conducting remote training sessions",
      type: "Workshop",
      price: "$29"
    },
    {
      id: "5",
      title: "Leadership Development Bootcamp",
      date: "April 18-20, 2024",
      time: "9:00 AM - 5:00 PM",
      location: "Chicago, IL",
      attendees: 150,
      image: "https://images.unsplash.com/photo-1517048676732-d65bc937f952?w=300&h=200&fit=crop",
      description: "3-day intensive bootcamp for emerging leaders",
      type: "Bootcamp",
      price: "$299"
    }
  ];

  const handleAttendEvent = (eventId) => {
    if (attendingEvents.includes(eventId)) {
      setAttendingEvents(attendingEvents.filter(id => id !== eventId));
      toast({
        title: "Event cancelled",
        description: "You are no longer attending this event.",
      });
    } else {
      setAttendingEvents([...attendingEvents, eventId]);
      toast({
        title: "Event registered!",
        description: "You have successfully registered for this event.",
      });
    }
  };

  const handleSaveEvent = (eventId) => {
    if (savedEvents.includes(eventId)) {
      setSavedEvents(savedEvents.filter(id => id !== eventId));
      toast({
        title: "Event unsaved",
        description: "Event removed from your saved events.",
      });
    } else {
      setSavedEvents([...savedEvents, eventId]);
      toast({
        title: "Event saved!",
        description: "Event added to your saved events.",
      });
    }
  };

  const filteredEvents = allEvents.filter(event =>
    event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    event.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
    event.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center text-lg">
            <Calendar className="w-6 h-6 mr-3 text-blue-600" />
            Training Events
          </CardTitle>
          <p className="text-sm text-gray-600">
            Discover and attend training events, conferences, and workshops
          </p>
        </CardHeader>
      </Card>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center space-x-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                type="text"
                placeholder="Search events by title, location, or topic..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button variant="outline" className="flex items-center">
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="upcoming" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="upcoming">Upcoming ({filteredEvents.length})</TabsTrigger>
          <TabsTrigger value="attending">Attending ({attendingEvents.length})</TabsTrigger>
          <TabsTrigger value="saved">Saved ({savedEvents.length})</TabsTrigger>
          <TabsTrigger value="past">Past Events</TabsTrigger>
        </TabsList>

        <TabsContent value="upcoming" className="space-y-4">
          {filteredEvents.map((event) => (
            <Card key={event.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start space-x-6">
                  <div 
                    className="w-32 h-24 bg-cover bg-center rounded-lg flex-shrink-0"
                    style={{ backgroundImage: `url(${event.image})` }}
                  >
                    <div className="w-full h-full bg-black/20 rounded-lg"></div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-semibold text-xl hover:text-blue-600 cursor-pointer mb-1">
                          {event.title}
                        </h3>
                        <div className="flex items-center space-x-4 text-sm text-gray-600 mb-2">
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 mr-1" />
                            {formatDate(event.date)}
                          </div>
                          <div className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            {event.time}
                          </div>
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                          <div className="flex items-center">
                            {event.location === 'Virtual Event' ? (
                              <Globe className="w-4 h-4 mr-1" />
                            ) : (
                              <MapPin className="w-4 h-4 mr-1" />
                            )}
                            {event.location}
                          </div>
                          <div className="flex items-center">
                            <Users className="w-4 h-4 mr-1" />
                            {event.attendees} attending
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge variant={event.type === 'Free' ? 'default' : 'outline'}>
                          {event.type}
                        </Badge>
                        {event.price && event.price !== 'Free' && (
                          <Badge variant="outline" className="text-green-600 border-green-600">
                            {event.price}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <p className="text-gray-700 mb-4">{event.description}</p>
                    <div className="flex items-center space-x-3">
                      <Button
                        onClick={() => handleAttendEvent(event.id)}
                        variant={attendingEvents.includes(event.id) ? "outline" : "default"}
                        size="sm"
                        className="flex items-center"
                      >
                        {attendingEvents.includes(event.id) ? "Attending" : "Attend"}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSaveEvent(event.id)}
                        className="flex items-center"
                      >
                        <Bookmark className={`w-4 h-4 mr-1 ${savedEvents.includes(event.id) ? 'fill-current' : ''}`} />
                        {savedEvents.includes(event.id) ? 'Saved' : 'Save'}
                      </Button>
                      <Button variant="outline" size="sm" className="flex items-center">
                        <ExternalLink className="w-4 h-4 mr-1" />
                        View details
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="attending" className="space-y-4">
          {attendingEvents.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">No events registered</h3>
                <p className="text-gray-600 mb-4">Register for training events to see them here.</p>
                <Button>Browse Events</Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredEvents.filter(event => attendingEvents.includes(event.id)).map((event) => (
                <Card key={event.id} className="hover:shadow-lg transition-shadow border-l-4 border-l-blue-600">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-6">
                      <div 
                        className="w-24 h-18 bg-cover bg-center rounded-lg flex-shrink-0"
                        style={{ backgroundImage: `url(${event.image})` }}
                      >
                        <div className="w-full h-full bg-black/20 rounded-lg"></div>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg hover:text-blue-600 cursor-pointer mb-1">
                          {event.title}
                        </h3>
                        <div className="flex items-center space-x-4 text-sm text-gray-600 mb-2">
                          <span>{formatDate(event.date)}</span>
                          <span>•</span>
                          <span>{event.time}</span>
                          <span>•</span>
                          <span>{event.location}</span>
                        </div>
                        <p className="text-sm text-gray-700 mb-3">{event.description}</p>
                        <div className="flex items-center space-x-2">
                          <Badge className="bg-green-100 text-green-800">Registered</Badge>
                          <Button variant="outline" size="sm" className="text-xs">
                            Add to calendar
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleAttendEvent(event.id)}
                            className="text-xs text-red-600 border-red-200 hover:bg-red-50"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="saved" className="space-y-4">
          {savedEvents.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Bookmark className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">No saved events</h3>
                <p className="text-gray-600 mb-4">Save interesting events to review them later.</p>
                <Button>Browse Events</Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredEvents.filter(event => savedEvents.includes(event.id)).map((event) => (
                <Card key={event.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4">
                        <div 
                          className="w-20 h-16 bg-cover bg-center rounded-lg flex-shrink-0"
                          style={{ backgroundImage: `url(${event.image})` }}
                        >
                          <div className="w-full h-full bg-black/20 rounded-lg"></div>
                        </div>
                        <div>
                          <h3 className="font-semibold hover:text-blue-600 cursor-pointer mb-1">
                            {event.title}
                          </h3>
                          <div className="text-sm text-gray-600 mb-2">
                            {formatDate(event.date)} • {event.location}
                          </div>
                          <p className="text-sm text-gray-700">{event.description}</p>
                        </div>
                      </div>
                      <div className="flex flex-col space-y-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleSaveEvent(event.id)}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                        >
                          Remove
                        </Button>
                        <Button size="sm">
                          Register
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="past" className="space-y-4">
          <Card>
            <CardContent className="p-8 text-center">
              <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="font-semibold text-lg mb-2">Past Events</h3>
              <p className="text-gray-600 mb-4">
                View recordings and materials from events you've attended.
              </p>
              <Badge variant="secondary" className="mb-4">Feature Coming Soon</Badge>
              <p className="text-sm text-gray-500">
                This feature will be available in the next update.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Events;